/** Configuración → Presupuestos SA → Costos de ejecución + fórmulas de superficie por familia. */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const errMsg = (e, d) => e?.response?.data?.detail || d;
const PARAMS = [
  ['costo_km', 'Costo logístico por km (Q)'], ['depreciacion_km', 'Depreciación por km (Q)'],
  ['hotel_persona_noche', 'Hotel por persona/noche (Q)'], ['alimentacion_persona_dia', 'Alimentación por persona/día (Q)'],
  ['mano_obra_persona_dia', 'Mano de obra por persona/día (Q)'], ['mano_obra_m2_vidrio', 'Mano de obra vidrio por m² (Q)'],
  ['peaje_viaje', 'Peaje por viaje (Q)'], ['gasolina_km', 'Gasolina por km (Q) — fórmula pendiente, dejar 0'],
];
const FLAGS = [
  ['transporte_por_vehiculo', 'Transporte × vehículos'],
  ['gasolina_por_vehiculo', 'Gasolina × vehículos'],
  ['depreciacion_por_vehiculo', 'Depreciación × vehículos'],
];

export const ConfigEjecucion = ({ familias, onClose }) => {
  const [par, setPar] = useState(null);
  const [fams, setFams] = useState([]);

  useEffect(() => {
    api.get('/sa/config-ejecucion').then((r) => setPar(r.data)).catch(() => toast.error('Error al cargar parámetros'));
    api.get('/sa/config-familias').then((r) => setFams(r.data.familias || [])).catch(() => {});
  }, []);

  const famCfg = (v) => fams.find((x) => x.familia === v) || { familia: v, formula_area: '', merma_default_pct: 0 };
  const setFam = (v, campo, val) => {
    const otras = fams.filter((x) => x.familia !== v);
    setFams([...otras, { ...famCfg(v), [campo]: val }]);
  };

  const guardar = async () => {
    try {
      const body = {};
      PARAMS.forEach(([k]) => { body[k] = Number(par[k] || 0); });
      FLAGS.forEach(([k]) => { body[k] = !!par[k]; });
      await api.put('/sa/config-ejecucion', body);
      for (const fam of familias) {
        const c = famCfg(fam.v);
        if (c.formula_area || c.merma_default_pct) {
          await api.put('/sa/config-familias', { familia: fam.v, formula_area: c.formula_area || '', merma_default_pct: Number(c.merma_default_pct || 0) });
        }
      }
      toast.success('Parámetros guardados — los presupuestos existentes conservan su snapshot');
      onClose();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[620px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Costos de ejecución — parámetros</DialogTitle></DialogHeader>
        {!par ? <p className="text-sm text-[var(--text-muted)]">Cargando…</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PARAMS.map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-bold text-[var(--text-muted)]">{label}</label>
                  <Input type="number" step="any" value={par[k]} onChange={(e) => setPar({ ...par, [k]: e.target.value })} className="h-9" data-testid={`cfgej-${k}`} />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              {FLAGS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!par[k]} onChange={(e) => setPar({ ...par, [k]: e.target.checked })} className="accent-[#0EA5E9]" data-testid={`cfgej-${k}`} />
                  {label}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-amber-500 font-semibold">⚠ Cambiar estos valores NO modifica presupuestos existentes (conservan su snapshot). Solo aplica a nuevos cálculos o al usar "Recalcular con costos actuales".</p>

            <div>
              <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)] mb-1">Fórmulas de superficie por familia</p>
              <div className="space-y-1.5 max-h-[30vh] overflow-y-auto">
                {familias.map((fam) => {
                  const c = famCfg(fam.v);
                  return (
                    <div key={fam.v} className="grid grid-cols-12 gap-1.5 items-center">
                      <span className="col-span-3 text-xs font-semibold">{fam.label}</span>
                      <Input value={c.formula_area || ''} onChange={(e) => setFam(fam.v, 'formula_area', e.target.value)}
                        placeholder="ej. ancho_m * largo_m" className="col-span-6 h-8 text-xs font-mono" data-testid={`cfgfam-formula-${fam.v}`} />
                      <Input type="number" value={c.merma_default_pct ?? 0} onChange={(e) => setFam(fam.v, 'merma_default_pct', e.target.value)}
                        placeholder="Merma %" className="col-span-3 h-8 text-xs" data-testid={`cfgfam-merma-${fam.v}`} />
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">La merma por familia se usa cuando el componente de la regla no define la suya.</p>
            </div>

            <Button onClick={guardar} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="cfgej-guardar">Guardar parámetros</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
